/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Wrappers around Word content controls (rich-text type).
 * Each control is tagged with an Ecsper namespace and ID.
 * Examples:
 *   - ecsper:sot:{uuid}      (Statement of Truth)
 *   - ecsper:declaration     (CPR Part 35 declaration)
 *   - ecsper:citation:{uuid} (Citation)
 *   - ecsper:vaultref:{uuid} (Vault reference)
 */

export interface ContentControlSpec {
  /** Full tag (e.g., 'ecsper:sot:abc-123') */
  tag: string;
  /** Display title shown in Word's properties pane */
  title: string;
  /** Initial text content */
  content?: string;
  /** Whether this control is locked (cannotEdit) */
  locked?: boolean;
}

export interface ContentControlRef {
  /** The Word content control ID (from Office.js) */
  id: number;
  tag: string;
  title: string;
  text: string;
  locked: boolean;
}

/**
 * Insert a content control at the current selection.
 * Returns the inserted control's ID and tag.
 */
export async function insertContentControl(spec: ContentControlSpec): Promise<ContentControlRef> {
  return Word.run(async (context) => {
    const range = context.document.getSelection();
    const cc = range.insertContentControl(Word.ContentControlType.richText);
    cc.tag = spec.tag;
    cc.title = spec.title;
    const locked = spec.locked ?? false;
    cc.cannotEdit = locked;
    cc.cannotDelete = locked;
    if (spec.content !== undefined) {
      cc.insertText(spec.content, Word.InsertLocation.replace);
    }
    cc.load(['id', 'tag', 'title', 'text', 'cannotEdit', 'cannotDelete']);
    await context.sync();
    return {
      id: cc.id,
      tag: cc.tag,
      title: cc.title,
      text: cc.text,
      locked: cc.cannotEdit,
    };
  });
}

/**
 * Find all content controls whose tag matches the given prefix.
 * Pass 'ecsper:citation:' to find all citations, for example.
 */
export async function findContentControlsByTag(
  tagPrefix: string,
): Promise<readonly ContentControlRef[]> {
  return Word.run(async (context) => {
    const ccs = context.document.contentControls;
    ccs.load('items');
    await context.sync();
    for (const cc of ccs.items) {
      cc.load(['id', 'tag', 'title', 'text', 'cannotEdit', 'cannotDelete']);
    }
    await context.sync();
    const refs: ContentControlRef[] = [];
    for (const cc of ccs.items) {
      if (typeof cc.tag === 'string' && cc.tag.startsWith(tagPrefix)) {
        refs.push({
          id: cc.id,
          tag: cc.tag,
          title: cc.title,
          text: cc.text,
          locked: cc.cannotEdit,
        });
      }
    }
    return refs;
  });
}

/**
 * Read the content of a control by its tag (exact match).
 */
export async function readContentControl(tag: string): Promise<ContentControlRef | null> {
  return Word.run(async (context) => {
    const ccs = context.document.contentControls;
    ccs.load('items');
    await context.sync();
    for (const cc of ccs.items) {
      cc.load(['id', 'tag', 'title', 'text', 'cannotEdit', 'cannotDelete']);
    }
    await context.sync();
    for (const cc of ccs.items) {
      if (cc.tag === tag) {
        return {
          id: cc.id,
          tag: cc.tag,
          title: cc.title,
          text: cc.text,
          locked: cc.cannotEdit,
        };
      }
    }
    return null;
  });
}

/**
 * Lock a content control (sets cannotEdit = true and cannotDelete = true).
 * Used by Statement of Truth signing.
 */
export async function lockContentControl(tag: string): Promise<void> {
  await Word.run(async (context) => {
    const ccs = context.document.contentControls;
    ccs.load('items');
    await context.sync();
    for (const cc of ccs.items) {
      cc.load(['tag']);
    }
    await context.sync();
    const found = ccs.items.find((cc) => cc.tag === tag);
    if (found === undefined) return;
    found.cannotEdit = true;
    found.cannotDelete = true;
    await context.sync();
  });
}
